import React, { useState, useEffect } from 'react';
import axiosInstance from '../../lib/axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
  ShoppingCart,
  Mail,
  Clock,
  User,
  Phone,
  Package,
  RefreshCw,
  Send,
  Eye,
  Trash2,
  DollarSign,
  AlertCircle,
  CheckCircle,
  Search,
  Filter
} from 'lucide-react';
import { toast } from 'sonner';
import { formatPrice } from '../../lib/utils';
import LoadingSpinner from '../../components/LoadingSpinner';

export default function AdminAbandonedCarts() {
  const [carts, setCarts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    total_abandoned: 0,
    total_value: 0,
    recovered: 0,
    recovery_rate: 0
  });
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCart, setSelectedCart] = useState(null);
  const [sendingReminder, setSendingReminder] = useState(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchAbandonedCarts();
  }, [filter]);

  const fetchAbandonedCarts = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`${API_URL}/api/admin/abandoned-carts`, {
        params: { status: filter !== 'all' ? filter : undefined }
      });
      setCarts(response.data.carts || []);
      setStats(response.data.stats || {
        total_abandoned: 0,
        total_value: 0,
        recovered: 0,
        recovery_rate: 0
      });
    } catch (error) {
      console.error('Failed to fetch abandoned carts:', error);
      toast.error('Failed to load abandoned carts');
    } finally {
      setLoading(false);
    }
  };

  const sendReminder = async (cartId) => {
    try {
      setSendingReminder(cartId);
      await axiosInstance.post(`${API_URL}/api/admin/abandoned-carts/${cartId}/send-reminder`);
      toast.success('Reminder sent successfully!');
      fetchAbandonedCarts();
    } catch (error) {
      toast.error('Failed to send reminder');
    } finally {
      setSendingReminder(null);
    }
  };

  const markAsRecovered = async (cartId) => {
    try {
      await axiosInstance.put(`${API_URL}/api/admin/abandoned-carts/${cartId}/recover`);
      toast.success('Cart marked as recovered');
      fetchAbandonedCarts();
    } catch (error) {
      toast.error('Failed to update cart');
    }
  };

  const deleteCart = async (cartId) => {
    if (!window.confirm('Are you sure you want to delete this abandoned cart record?')) return;
    try {
      await axiosInstance.delete(`${API_URL}/api/admin/abandoned-carts/${cartId}`);
      toast.success('Cart deleted');
      fetchAbandonedCarts();
    } catch (error) {
      toast.error('Failed to delete cart');
    }
  };

  const getTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  const filteredCarts = carts.filter(cart => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      cart.customer_email?.toLowerCase().includes(search) ||
      cart.customer_phone?.includes(search) ||
      cart.customer_name?.toLowerCase().includes(search)
    );
  });

  if (loading) return <LoadingSpinner />;

  return (
    <div className="p-6 space-y-6" data-testid="abandoned-carts-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <ShoppingCart className="w-7 h-7 text-orange-600" />
            Abandoned Carts
          </h1>
          <p className="text-gray-500 mt-1">Recover lost sales by following up with customers</p>
        </div>
        <Button onClick={fetchAbandonedCarts} variant="outline">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Abandoned</p>
                <p className="text-2xl font-bold text-orange-600">{stats.total_abandoned}</p>
              </div>
              <ShoppingCart className="w-8 h-8 text-orange-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Lost Value</p>
                <p className="text-2xl font-bold text-red-600">{formatPrice(stats.total_value)}</p>
              </div>
              <DollarSign className="w-8 h-8 text-red-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Recovered</p>
                <p className="text-2xl font-bold text-green-600">{stats.recovered}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Recovery Rate</p>
                <p className="text-2xl font-bold text-blue-600">{stats.recovery_rate}%</p>
              </div>
              <AlertCircle className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            placeholder="Search by email, phone, or name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <div className="flex gap-2">
          {['all', 'pending', 'reminded', 'recovered'].map((status) => (
            <Button
              key={status}
              variant={filter === status ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter(status)}
              className={filter === status ? 'bg-orange-600 hover:bg-orange-700' : ''}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </Button>
          ))}
        </div>
      </div>

      {/* Carts List */}
      <div className="space-y-4">
        {filteredCarts.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center">
              <ShoppingCart className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No abandoned carts found</p>
            </CardContent>
          </Card>
        ) : (
          filteredCarts.map((cart) => (
            <Card key={cart.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                      <User className="w-6 h-6 text-orange-600" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-gray-900">
                          {cart.customer_name || 'Guest User'}
                        </h3>
                        <Badge variant={
                          cart.status === 'recovered' ? 'success' :
                          cart.status === 'reminded' ? 'warning' : 'secondary'
                        }>
                          {cart.status}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-gray-500 mt-1">
                        {cart.customer_email && (
                          <span className="flex items-center gap-1">
                            <Mail className="w-3 h-3" />
                            {cart.customer_email}
                          </span>
                        )}
                        {cart.customer_phone && (
                          <span className="flex items-center gap-1">
                            <Phone className="w-3 h-3" />
                            {cart.customer_phone}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-4 text-sm text-gray-500 mt-1">
                        <span className="flex items-center gap-1">
                          <Package className="w-3 h-3" />
                          {cart.items_count} items
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {getTimeAgo(cart.abandoned_at)}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="text-sm text-gray-500">Cart Value</p>
                      <p className="text-xl font-bold text-gray-900">{formatPrice(cart.total_value)}</p>
                    </div>

                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setSelectedCart(selectedCart === cart.id ? null : cart.id)}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      {cart.status !== 'recovered' && (
                        <>
                          <Button
                            size="sm"
                            onClick={() => sendReminder(cart.id)}
                            disabled={sendingReminder === cart.id}
                            className="bg-blue-600 hover:bg-blue-700"
                          >
                            {sendingReminder === cart.id ? (
                              <RefreshCw className="w-4 h-4 animate-spin" />
                            ) : (
                              <Send className="w-4 h-4" />
                            )}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => markAsRecovered(cart.id)}
                            className="text-green-600 border-green-600 hover:bg-green-50"
                          >
                            <CheckCircle className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => deleteCart(cart.id)}
                        className="text-red-600 border-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Expanded Cart Items */}
                {selectedCart === cart.id && (
                  <div className="mt-4 pt-4 border-t">
                    <h4 className="font-medium text-gray-900 mb-3">Cart Items</h4>
                    <div className="space-y-2">
                      {cart.items?.map((item, idx) => (
                        <div key={idx} className="flex items-center gap-4 p-2 bg-gray-50 rounded-lg">
                          {item.image && (
                            <img src={item.image} alt={item.name} className="w-12 h-12 object-cover rounded" />
                          )}
                          <div className="flex-1">
                            <p className="font-medium text-gray-900">{item.name}</p>
                            <p className="text-sm text-gray-500">Qty: {item.quantity}</p>
                          </div>
                          <p className="font-medium">{formatPrice(item.price * item.quantity)}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
}
